import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Table, Button, Row, Col, Typography, Input, App, Grid, Tooltip, Drawer} from "antd";
import { ArrowLeftOutlined, EditOutlined, PlusOutlined, SearchOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchJournals, deleteJournal } from "./journalThunk";
import {
  resetJournals,
  setSelectedJournal,
  setIsEditJournal,
  setRefreshList,
} from "./journalSlice";
import { dateFormat, formatDateOnly, getAccessType } from "../../lib/functions";
import commaNumber from "comma-number";
import { DeleteOutlined } from "@ant-design/icons";
import { useIsAdminUser } from "../../lib/accessList";
import { useTxLock } from "../../hooks/useTxLock";

const { Title } = Typography;

const format = commaNumber.bindWith(",", ".");

const JournalList = () => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list, loading, refreshList } = useSelector((state) => state.journals);
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  let [access_type, access_type_set] = useState("");
  const isAdminUser = useIsAdminUser();

  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  useEffect(() => {
    const access = getAccessType("journal_entry");
    access_type_set(access);
  }, []);
  useEffect(() => {
    dispatch(resetJournals());
    dispatch(fetchJournals());
  }, [dispatch]);

  useEffect(() => {
    if (refreshList) {
      dispatch(fetchJournals());
      dispatch(setRefreshList(false));
    }
  }, [refreshList, dispatch]);
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  const filteredJournals = useMemo(() => {
    if (!searchText.trim()) return list;

    return list.filter((j) =>
      Object.values(j || {})
        .join(" ")
        .toLowerCase()
        .includes(searchText?.toLowerCase())
    );
  }, [list, searchText]);

  const handleDelete = async (jrn_id) => {
    try {
      await dispatch(deleteJournal(jrn_id)).unwrap();
      modal.success({
        title: "Journal entry deleted successfully",
      });
      dispatch(fetchJournals());
    } catch (error) {
      modal.warning({
        title: error.message || "Delete failed",
      });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Journal Code",
      dataIndex: "jrn_code",
      sorter: (a, b) => (a.jrn_code || "").localeCompare(b.jrn_code || ""),
    },
    {
      title: "Journal Date",
      dataIndex: "created_date",
      render: (val) => formatDateOnly(val),
      sorter: (a, b) =>
        new Date(a.created_date).getTime() - new Date(b.created_date).getTime(),
    },
    {
      title: "Debit Total",
      dataIndex: "debit_total",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
      sorter: (a, b) => (a.debit_total || 0) - (b.debit_total || 0),
    },
    {
      title: "Credit Total",
      dataIndex: "credit_total",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
      sorter: (a, b) => (a.credit_total || 0) - (b.credit_total || 0),
    },
    {
      title: "Description",
      dataIndex: "narration",
    },
    {
      title: "Created By",
      dataIndex: "full_name",
      align: "left",
      filters: [...new Set(list.map((j) => j.full_name).filter(Boolean))].map(
        (name) => ({
          text: name,
          value: name,
        })
      ),
      onFilter: (value, record) => record.full_name === value,
    },
 {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.created_date);

    return (
      <>
        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            disabled={!(isAdminUser || canEdit) || txLocked}
            onClick={() => {
              if (txLocked) return;

              dispatch(setSelectedJournal(record));
              dispatch(setIsEditJournal(true));

              navigate(`/accounts/journal-entry/add`);
            }}
            icon={<EditOutlined />}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            disabled={!(isAdminUser || canDelete) || txLocked}
            onClick={() => {
              if (txLocked) return;

              modal.confirm({
                title: "Are you sure?",
                content: `This will delete Journal entry with amount: ${record.credit_total}`,
                okText: "Yes, Delete",
                cancelText: "Cancel",
                onOk: () => handleDelete(record.jrn_id),
              });
            }}
          />
        </Tooltip>
      </>
    );
  },
}
  ];

  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Account", dataIndex: "acc_name" },
    {
      title: "Credit Amount",
      dataIndex: "credit_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
    {
      title: "Debit Amount",
      dataIndex: "debit_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
  ];

  return (
    <>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "12px",
      }}
    >
      <Button
        type="text"
        icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
        onClick={() => navigate(-1)}
        data-testid="journal-list-back-button"
      />
      <Title level={3} style={{ margin: 0 }}>
        Journal / Double Entry
      </Title>

      <div style={{ marginLeft: "auto" }}>
        <Tooltip title="Help">
          <Button
            type="text"
            icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
            onClick={() => setHelpOpen(true)}
          />
        </Tooltip>
      </div>
    </div>
      <Row
        justify="space-between"
        align="middle"
        style={{
          margin: "32px 0",
          gap: !screens.md && "12px",
          flexWrap: !screens.md && "nowrap",
        }}
      >
        {/* <Col>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col> */}
        <Col>
          <Input
            placeholder="Search journals..."
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: screens.md ? 250 : 200 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>

        <Col>
          {access_type == "WRITE" && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                dispatch(setSelectedJournal(null));
                dispatch(setIsEditJournal(false));
                navigate("/accounts/journal-entry/add");
              }}
              data-testid="btn-add-new-journal"
            >
              Add New
            </Button>
          )}
        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={filteredJournals}
        rowKey="jrn_id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: filteredJournals.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_, size) => {
            setPageSize(size); // ✅ THIS FIXES IT
            setCurrentPage(1); // reset to page 1 (best UX)
          },
        }}
        size="middle"
        scroll={{ x: "max-content" }}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <Table
                rowKey="detailId"
                columns={detailColumns}
                dataSource={record.details || []}
                pagination={false}
                size="small"
                bordered
              />
            );
          },
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
      <Drawer
        title="Journal Entry Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Journal / Double Entry Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Journal Entry module is used to record accounting double-entry
            transactions within the ERP system. It helps maintain accurate debit
            and credit balances for accounting operations and financial reporting.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View journal transaction records</li>
              <li>Use <strong>Add New</strong> to create journal entries</li>
              <li>Edit journal details when required</li>
              <li>Delete journal entries if permitted</li>
              <li>Search journal transactions using filters</li>
              <li>Track debit and credit account movements</li>
              <li>Review detailed account allocations using expandable rows</li>
              <li>Monitor narration and journal totals</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Debit and credit totals should remain balanced.</li>
              <li>Journal entries affect accounting ledgers immediately.</li>
              <li>Expandable rows display detailed account transactions.</li>
              <li>Search and filters help locate journal records quickly.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Transaction history is maintained for auditing and reports.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default JournalList;
